'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { BenefitsSection } from '@/types/sanity';
import { Badge } from '@/components/ui/badge';
import { icons, type LucideIcon } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';

import { cn } from '@/lib/utils';

function LucideIconByName({ name, className }: { name: string; className?: string }) {
  const Icon: LucideIcon | undefined = icons[name as keyof typeof icons];
  if (!Icon) return null;
  return <Icon className={className} />;
}

interface BenefitsSectionComponentProps {
  section: BenefitsSection;
  pageSlug?: string;
}

export function BenefitsSectionComponent({ section, pageSlug }: BenefitsSectionComponentProps) {
  const layout = section.layout || 'grid-3';

  const getGridClass = () => {
    switch (layout) {
      case 'grid-2':
        return 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto';
      case 'grid-3':
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto';
      case 'grid-4':
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto';
      case 'list':
        return 'space-y-6 max-w-4xl mx-auto';
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto';
    }
  };

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'benefits-section py-8 md:py-12 lg:py-16',
          pageSlug && `benefits-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader
              title={section.title}
              subtitle={section.subtitle}
              description={section.description}
            />

            <StaggerContainer staggerDelay={0.15} className={getGridClass()}>
              {section.benefits?.map((benefit) => (
                <motion.div
                  key={benefit._id}
                  variants={staggerItemVariants}
                  className="text-center"
                >
                  {benefit.icon && (
                    <LucideIconByName
                      name={benefit.icon}
                      className="h-10 w-10 text-primary mx-auto mb-4"
                    />
                  )}
                  <h3 className="font-semibold text-lg mb-1">{benefit.title}</h3>
                  {benefit.subtitle && (
                    <p className="text-sm text-muted-foreground mb-3">{benefit.subtitle}</p>
                  )}
                  {benefit.description && (
                    <div className="prose prose-sm max-w-none text-muted-foreground">
                      <PortableText value={benefit.description} />
                    </div>
                  )}
                  {benefit.statistic && (
                    <div className="mt-4">
                      <Badge variant="secondary" className="text-lg px-3 py-1">
                        {benefit.statistic}
                      </Badge>
                    </div>
                  )}
                </motion.div>
              ))}
            </StaggerContainer>

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
